import { useState, useRef, useCallback } from 'react';
import { fetchWithAuth } from '../../lib/api';

type FeedbackType = 'bug' | 'feature' | 'question' | 'feedback';

interface Screenshot {
  filename: string;
  dataUrl: string;
}

interface FeedbackModalProps {
  onClose: () => void;
}

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'question', label: 'Question' },
  { value: 'feedback', label: 'General Feedback' },
];

async function compressImage(file: File): Promise<{ dataUrl: string; filename: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const MAX_W = 1280;
      const MAX_H = 900;
      let { width, height } = img;

      if (width > MAX_W) {
        height = Math.round((height * MAX_W) / width);
        width = MAX_W;
      }
      if (height > MAX_H) {
        width = Math.round((width * MAX_H) / height);
        height = MAX_H;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      const safeName = file.name.replace(/\.[^.]+$/, '.jpg');
      resolve({ dataUrl, filename: safeName });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load image'));
    };

    img.src = objectUrl;
  });
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ issueUrl: string; issueNumber: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(async (files: File[]) => {
    const images = files.filter(f => f.type.startsWith('image/'));
    const slots = 5 - screenshots.length;
    const toAdd = images.slice(0, slots);

    for (const file of toAdd) {
      try {
        const { dataUrl, filename } = await compressImage(file);
        setScreenshots(prev => [...prev, { filename, dataUrl }]);
        setPreviewUrls(prev => [...prev, dataUrl]);
      } catch {
        // skip unreadable images silently
      }
    }
  }, [screenshots.length]);

  const removeScreenshot = useCallback((index: number) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const REST_API_URL = import.meta.env.VITE_REST_API_URL as string;
      const resp = await fetchWithAuth(`${REST_API_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          screenshots: screenshots.map(s => ({ filename: s.filename, dataUrl: s.dataUrl })),
          context: {
            product: 'mlh',
            appName: 'My Life History',
            url: window.location.pathname,
            userAgent: navigator.userAgent,
            appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0',
          },
        }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({ error: 'Submission failed' })) as { error?: string };
        setError(errData.error ?? 'Submission failed. Please try again.');
        return;
      }

      const data = await resp.json() as { issueUrl: string; issueNumber: number };
      setResult(data);
    } catch {
      setError('Network error — please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = title.trim().length >= 3 && description.trim().length >= 10 && !submitting;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-stone-900">Submit Feedback</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600 p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {result ? (
            /* ── Success state ── */
            <div className="text-center space-y-4 py-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-green-600">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-stone-900 text-lg">Feedback submitted!</p>
                <p className="text-stone-500 text-sm mt-1">Issue #{result.issueNumber} has been created.</p>
              </div>
              <a
                href={result.issueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium underline-offset-2 hover:underline"
              >
                View on GitHub
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M15.75 2.25H21a.75.75 0 01.75.75v5.25a.75.75 0 01-1.5 0V4.81L8.03 17.03a.75.75 0 01-1.06-1.06L19.19 3.75h-3.44a.75.75 0 010-1.5zm-10.5 4.5a1.5 1.5 0 00-1.5 1.5v10.5a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V10.5a.75.75 0 011.5 0v8.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V8.25a3 3 0 013-3H13.5a.75.75 0 010 1.5H5.25z" clipRule="evenodd" />
                </svg>
              </a>
              <button
                type="button"
                onClick={onClose}
                className="block w-full py-2 text-stone-400 hover:text-stone-600 text-sm transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            /* ── Form ── */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type */}
              <div>
                <span className="block text-sm font-medium text-stone-700 mb-2">Type</span>
                <div className="grid grid-cols-2 gap-2">
                  {TYPE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        type === opt.value
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-white text-stone-700 border-stone-200 hover:border-amber-300 hover:bg-amber-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="fb-title" className="block text-sm font-medium text-stone-700 mb-1">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  id="fb-title"
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Brief summary"
                  maxLength={200}
                  autoComplete="off"
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="fb-desc" className="block text-sm font-medium text-stone-700 mb-1">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="fb-desc"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What happened? What did you expect? Steps to reproduce if it's a bug…"
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-stone-900 placeholder-stone-400 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
              </div>

              {/* Screenshots */}
              <div>
                <span className="block text-sm font-medium text-stone-700 mb-2">
                  Screenshots <span className="text-stone-400 font-normal">(optional · up to 5)</span>
                </span>

                {previewUrls.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {previewUrls.map((url, i) => (
                      <div key={i} className="relative group w-20 h-20">
                        <img
                          src={url}
                          alt={`Screenshot ${i + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border border-stone-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity leading-none"
                          aria-label={`Remove screenshot ${i + 1}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {screenshots.length < 5 && (
                  <div
                    role="button"
                    tabIndex={0}
                    onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
                      isDragOver
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-stone-200 hover:border-amber-300 hover:bg-stone-50'
                    }`}
                  >
                    <p className="text-sm text-stone-500">
                      Drag & drop or{' '}
                      <span className="text-amber-600 font-medium">click to browse</span>
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">PNG, JPG, WebP · auto-compressed</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={e => {
                        if (e.target.files) addFiles(Array.from(e.target.files));
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Auto-captured info */}
              <p className="text-xs text-stone-400 bg-stone-50 rounded-lg px-3 py-2">
                Automatically included: current page, browser info, and app version.
              </p>

              {error && (
                <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-3 px-4 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
              >
                {submitting ? 'Submitting…' : 'Submit Feedback'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
